import { useState, useEffect, useMemo } from 'react'
import { FaPencil } from "react-icons/fa6";
import { useDatabase } from "../contexts/DatabaseContext";
import { useAuth } from "../contexts/AuthContext";
import WorkspaceDesignTab from "./WorkspaceDesignTab";
import WorkspaceSettingsTab from "./WorkspaceSettingsTab";
import WorkspaceChannelsTab from "./WorkspaceChannelsTab";
import WorkspaceAppsTab from "./WorkspaceAppsTab";
import { useAuthPost } from "../hooks/useAxios";
import { Accordion, Button, Card } from "../components/primitives/";
import ServiceIcons from "../assets/service-icons";
import styles from './Workspace.module.css';
import { useTranslation } from '../hooks/useTranslation';
import Modal from "./portals/Modal";
import Resubscribe from "./popup/Resubscribe";
import DeleteWorkspace from "./popup/DeleteWorkspace";
import { useOutletContext } from 'react-router-dom';
import { catchErrorParse } from '../ErrorUtility';
import ThreeDotsLoading from './primitives/ThreeDotsLoading.jsx';

export default function Workspace({ workspace, workspaceId, forcedOpen = false, clientData }) {

    const {config, loading: configLoading, setConfig, error: configError} = useWorkspaceConfigDoc(workspaceId);
    const configNullCheck = configLoading || configError || !config;

    // Apps Tab
    const [newSubscriptionItem, setNewSubscriptionItem] = useState(config?.subscription);
    const currentSubscriptionItem = useMemo(() => {
        return clientData?.subscriptions.find(sub => sub.workspace === workspaceId);
    }, [clientData, workspaceId]);

    const hasChangesApps = useMemo(() => {
        if (configNullCheck) return false;
        if (!currentSubscriptionItem || !newSubscriptionItem)
            return false;
        for (let i = 0; i < currentSubscriptionItem.addons.length; i++) {
            if (currentSubscriptionItem.addons[i].enabled !== newSubscriptionItem.addons[i].enabled)
                return true;
        }
        return false;
    }, [currentSubscriptionItem, newSubscriptionItem, configNullCheck]);

    // Design Tab
    const [selectedColor, setSelectedColor] = useState(config?.workspaceStyles?.colors);
    const [customIconUrl, setCustomIconUrl] = useState(config?.workspaceStyles?.customIcon);
    const [iconFile, setIconFile] = useState(null);
    const [workspaceStyles, setWorkspaceStyles] = useState(config?.workspaceStyles);

    const hasChangesDesign = useMemo(() => {
        if (configNullCheck) return false;
        if (!workspaceStyles || !selectedColor) return false;
        // Check if colors have changed
        const colorChanged = selectedColor.name !== config.workspaceStyles.colors.name;
        
        // Check if icon file has changed
        const iconFileChanged = iconFile !== null;

        const iconSettingChanged = workspaceStyles.iconSetting1.iconSetting !== config.workspaceStyles.iconSetting1.iconSetting ||
            workspaceStyles.iconSetting2.iconSetting !== config.workspaceStyles.iconSetting2.iconSetting ||
            workspaceStyles.iconSetting3.iconSetting !== config.workspaceStyles.iconSetting3.iconSetting ||
            workspaceStyles.iconSetting1.selectedIcon !== config.workspaceStyles.iconSetting1.selectedIcon ||
            workspaceStyles.iconSetting2.selectedIcon !== config.workspaceStyles.iconSetting2.selectedIcon ||
            workspaceStyles.iconSetting3.selectedIcon !== config.workspaceStyles.iconSetting3.selectedIcon;
        
        return colorChanged || iconFileChanged || iconSettingChanged;
    }, [selectedColor, iconFile, workspaceStyles, configNullCheck, config]);

    // START OF SETTINGS TAB
    // Baseline Settings
    const [baseSettings, setBaseSettings] = useState(null);

    const hasChangesBaseline = useMemo(() => {
        if (configNullCheck || !baseSettings) return false;
        return Object.keys(baseSettings).some(key => JSON.stringify(baseSettings[key]) !== JSON.stringify(config[key]));
    }, [baseSettings, config, configNullCheck]);

    // File Upload Settings
    const [allowedFileTypes, setAllowedFileTypes] = useState(config?.allowedFileTypes);

    const hasChangesFileUpload = useMemo(() => {
        if (configNullCheck || !allowedFileTypes) return false;
        return JSON.stringify(allowedFileTypes) !== JSON.stringify(config?.allowedFileTypes);
    }, [allowedFileTypes, config, configNullCheck]);

    // Auto Engage Settings
    const [autoEngages, setAutoEngages] = useState(config?.autoEngages);
    const [delay, setDelay] = useState(config?.autoEngageDelay);

    const hasChangesAutoEngage = useMemo(() => {
        if (configNullCheck || !autoEngages || !delay) return false;
        return delay !== config?.autoEngageDelay ||
            JSON.stringify(autoEngages) !== JSON.stringify(config?.autoEngages);
    }, [autoEngages, delay, config, configNullCheck]);

    // Auto Reply Settings
    const [autoReplies, setAutoReplies] = useState(config?.autoReplies);

    const hasChangesAutoReply = useMemo(() => {
        if (configNullCheck || !autoReplies) return false;
        return JSON.stringify(autoReplies) !== JSON.stringify(config?.autoReplies);
    }, [autoReplies, config, configNullCheck]);

    // Contact Module Settings
    const [contactModuleSettings, setContactModuleSettings] = useState(config?.contactModuleSettings);
    const [recaptchaPrivateKey, setRecaptchaPrivateKey] = useState(workspace?.recaptchaPrivateKey);

    const hasChangesContactModule = useMemo(() => {
        if (configNullCheck || !contactModuleSettings || !workspace) return false;
        return JSON.stringify(contactModuleSettings) !== JSON.stringify(config?.contactModuleSettings) || recaptchaPrivateKey !== workspace?.recaptchaPrivateKey;
    }, [contactModuleSettings, config, configNullCheck, recaptchaPrivateKey, workspace]);
    // END OF SETTINGS TAB
    
    const [choice, setChoice] = useState("Channels");
    const t = useTranslation();
    const { getInvoiceData, mobile, setVisualizerConfig, requestedVisualTrigger, requestedVisualIndex, getBalance } = useOutletContext();
    const { createSubscriptionActionEvent, createWorkspaceActionEvent } = useDatabase();

    const [states, setStates] = useState({ isOpen: false, option: 0 });
    
    const { url, teamsUrl } = useAuth();
    const { post } = useAuthPost();

    const serviceIcon = useMemo(() => {
        if (workspace?.icon?.image_102) {
            return workspace.icon.image_102;
        } else if (workspace?.icon && workspace.workspaceType === 2) {
            return `https://cdn.discordapp.com/icons/${workspaceId}/${workspace.icon}`;
        } else if (workspace?.icon && (workspace.workspaceType === 1 || workspace.workspaceType === 3)) {
            return workspace.icon;
        } else {
            switch (workspace?.workspaceType) {
                case 0:
                    return ServiceIcons["slack.png"];
                case 1:
                    return ServiceIcons["teams.png"];
                case 2:
                    return ServiceIcons["discord.png"];
                default:
                    return null;
            }
        }
    }, [workspace]);

    useEffect(() => {
        if (configNullCheck) return;
        setNewSubscriptionItem(structuredClone(config.subscription));
        setSelectedColor(structuredClone(config.workspaceStyles.colors));
        setCustomIconUrl(config.workspaceStyles.customIcon); // URL is primitive, no need to clone
        setWorkspaceStyles(structuredClone(config.workspaceStyles));
        setBaseSettings({
            chatTitle: config.chatTitle, //Primitive
            newMessageTitle: config.newMessageTitle, //Primitive
            welcomeMsg: config.welcomeMsg, //Primitive
            contactSetting: config.contactSetting, //Primitive
            businessHourSetting: config.businessHourSetting, //Primitive
            timezone: structuredClone(config.timezone),
            businessHours: structuredClone(config.businessHours),
            showOutsideHours: config.showOutsideHours, //Primitive
            offlineWelcomeMsg: config.offlineWelcomeMsg, //Primitive
        });
        setAllowedFileTypes(structuredClone(config.allowedFileTypes));
        setAutoEngages(structuredClone(config.autoEngages));
        setDelay(config.autoEngageDelay); //Primitive
        setAutoReplies(structuredClone(config.autoReplies));
        setContactModuleSettings(structuredClone(config.contactModuleSettings));
        setRecaptchaPrivateKey(workspace?.recaptchaPrivateKey); // Primitive
    }, [configNullCheck]);

    useEffect(() => {
        if (configNullCheck) return;
        if (!config.visualizerEnabled) { setVisualizerConfig(null); return; }
        if (hasChangesApps || hasChangesDesign || hasChangesBaseline || hasChangesFileUpload || hasChangesAutoEngage || hasChangesAutoReply || hasChangesContactModule) {
            const newConfig = {
                ...config,
                ...baseSettings,
                allowedFileTypes: allowedFileTypes,
                autoEngages: autoEngages,
                autoEngageDelay: delay,
                autoReplies: autoReplies,
                subscription: newSubscriptionItem,
                workspaceStyles: {
                    ...workspaceStyles,
                    colors: selectedColor,
                    customIcon: customIconUrl,
                },
                contactModuleSettings: contactModuleSettings,
            };
            setVisualizerConfig(newConfig);
        } else {
            setVisualizerConfig(null);
        }
    }, [
        // Changes
        hasChangesApps,
        hasChangesDesign,
        hasChangesBaseline,
        hasChangesFileUpload,
        hasChangesAutoEngage,
        hasChangesAutoReply,
        hasChangesContactModule,
        // Null Check
        configNullCheck,
        // State
        selectedColor,
        customIconUrl,
        workspaceStyles,
        newSubscriptionItem,
        baseSettings,
        allowedFileTypes,
        autoEngages,
        delay,
        autoReplies,
        contactModuleSettings,
        recaptchaPrivateKey,
        // Config
        config,
        // Workspace
        workspace,
        // Set Visualizer Config
        setVisualizerConfig,
    ])

    const [loading, setLoading] = useState(false);

    async function handleResubscribeSubscription() {
        setLoading(true);
        setStates({ isOpen: false, option: 0 });
        const [result, error, errorCode, errorMessage] = await catchErrorParse(post, `${url}/webapp/subscriptions/re-subscribe`, {
            unsubbedSubscriptionItem: workspace.unsubbedSubscriptionItem,
        });
        if (error) {
            window.dispatchEvent(new CustomEvent('masterError', {
                detail: {
                    code: errorCode,
                    message: errorMessage ? errorMessage : error.message,
                    action: "resubscribe",
                }
            }));
        } else {
            getInvoiceData();
            getBalance();
            createSubscriptionActionEvent("resubscribe", result.latest_invoice.total, workspace?.workspaceType, workspaceId);
        }
        setLoading(false);
    }

    async function handleDeleteWorkspaceSubscription() {
        setLoading(true);
        setStates({ isOpen: false, option: 0 });
        const [result, error, errorCode, errorMessage] = await catchErrorParse(post, `${url}/webapp/subscriptions/del-workspace`, {
            unsubbedSubscriptionItem: workspace.unsubbedSubscriptionItem,
        });
        if (error) {
            window.dispatchEvent(new CustomEvent('masterError', {
                detail: {
                    code: errorCode,
                    message: errorMessage ? errorMessage : error.message,
                    action: "delete",
                    diabolical: true,
                }
            }));
        } else {
            getInvoiceData();
            createWorkspaceActionEvent("delete", workspace?.workspaceType, workspaceId);
        }
        setLoading(false);
    }

    const props = {
        url,
        teamsUrl,
        newSubscriptionItem,
        setNewSubscriptionItem,
        config,
        configLoading,
        workspace,
        workspaceType: workspace?.workspaceType,
        workspaceId,
        post,
        icon: serviceIcon,
        clientData,
        catchErrorParse,
        setConfig,
        currentSubscriptionItem,
        requestedVisualTrigger,
    };

    const designProps = {
        selectedColor,
        setSelectedColor,
        customIconUrl,
        setCustomIconUrl,
        iconFile,
        setIconFile,
        workspaceStyles,
        setWorkspaceStyles,
        hasChangesDesign,
    };

    const baselineProps = {
        baseSettings,
        setBaseSettings,
        hasChangesBaseline,
    };

    const fileUploadProps = {
        allowedFileTypes,
        setAllowedFileTypes,
        hasChangesFileUpload,
    };

    const autoEngageProps = {
        autoEngages,
        setAutoEngages,
        hasChangesAutoEngage,
        delay,
        setDelay,
        requestedVisualIndex,
    };

    const autoReplyProps = {
        autoReplies,
        setAutoReplies,
        hasChangesAutoReply,
        requestedVisualIndex,
    };

    const contactModuleProps = {
        contactModuleSettings,
        setContactModuleSettings,
        recaptchaPrivateKey,
        setRecaptchaPrivateKey,
        hasChangesContactModule,
    };

    function getChoice() {
        if (configError) return <div>Could not load workspace configuration, please try again later.</div>;
        if (configNullCheck) return <ThreeDotsLoading />;
        switch (choice) {
            case "Channels":
                return <WorkspaceChannelsTab {...props} />;
            case "Design":
                return <WorkspaceDesignTab {...props} {...designProps} />;
            case "Settings":
                return <WorkspaceSettingsTab
                    {...props}
                    baselineProps={baselineProps}
                    fileUploadProps={fileUploadProps} 
                    autoEngageProps={autoEngageProps}
                    autoReplyProps={autoReplyProps}
                    contactModuleProps={contactModuleProps}
                />;
            case "Apps":
                return <WorkspaceAppsTab {...props} hasChangesApps={hasChangesApps} />;
            default:
                return <WorkspaceChannelsTab {...props} />;
        }
    }

    return (
        <>
        <div className={styles.container}>
            <div className={mobile ? styles.interiorMobile : styles.interior}>
                <Accordion.Item key={workspaceId}>
                    <Accordion.Header noarrow={forcedOpen} disabled={forcedOpen} ignoreClasses={['workspace-name-container']}>
                        <img className={styles.serviceIcon} src={serviceIcon} alt="test" />
                        <WorkspaceNameField 
                            workspaceName={clientData?.workspaces[workspaceId]?.workspaceName || "Workspace Name"} 
                            workspaceId={workspaceId}
                        />
                    </Accordion.Header>
                    <Accordion.Body startOpen={forcedOpen}>
                        {workspace?.unsubbedSubscriptionItem && (workspace.unsubbedSubscriptionItem.currentPeriodEnd === null || workspace.unsubbedSubscriptionItem.currentPeriodEnd >= Math.floor(Date.now() / 1000)) ?
                        <Card ctype={`noborder ${mobile ? 'mobile-padding' : ''}`}>
                            <Card.Body>
                                <div className='workspace-subscription-row'>
                                    <Button className='workspace-subscription-button unregistered-layout' loading={loading} onClick={() => setStates({ isOpen: true, option: 0 })}>{t('Resubscribe')}</Button>
                                    <Button className='workspace-subscription-button delete unregistered-layout' loading={loading} onClick={() => setStates({ isOpen: true, option: 1 })}>{t('Delete Workspace')}</Button>
                                </div>
                            </Card.Body>
                        </Card>
                        :
                        <Card ctype={`noborder ${mobile ? 'mobile-padding' : ''}`}>
                            <Card.Header style={{ paddingBottom: '5px' }}>
                                <div className={styles.tabBar}>
                                    <div 
                                        className={`${styles.tabButton}${choice === "Channels" ? ` ${styles.selected}` : ""}`} 
                                        onClick={() => setChoice("Channels")}
                                    >
                                        {t('Channels')}
                                    </div>
                                    <div 
                                        className={`${styles.tabButton}${choice === "Design" ? ` ${styles.selected}` : ""}`} 
                                        onClick={() => setChoice("Design")}
                                    >
                                        {t('Design')}
                                    </div>
                                    <div 
                                        className={`${styles.tabButton}${choice === "Apps" ? ` ${styles.selected}` : ""}`} 
                                        onClick={() => setChoice("Apps")}
                                    >
                                        {t('Apps')}
                                    </div>
                                    <div 
                                        className={`${styles.tabButton}${choice === "Settings" ? ` ${styles.selected}` : ""}`} 
                                        onClick={() => setChoice("Settings")}
                                    >
                                        {t('Settings')}
                                    </div>
                                </div>
                            </Card.Header>
                            <Card.Body>
                                {getChoice()}
                            </Card.Body>
                        </Card>}
                    </Accordion.Body>
                </Accordion.Item>
            </div>
        </div>
        <Modal open={states.isOpen} onClickOutside={() => setStates({ isOpen: false, option: 0 })}>
            {
                states.option === 0 ? 
                    <Resubscribe onConfirm={handleResubscribeSubscription} onCancel={() => setStates({ isOpen: false, option: 0 })} 
                        totalCost={new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
                            ((workspace?.unsubbedSubscriptionItem?.cost || 0) + 
                            (Array.isArray(workspace?.unsubbedSubscriptionItem?.addons) ? 
                                workspace?.unsubbedSubscriptionItem?.addons.reduce((sum, addon) => 
                                    (addon && addon.enabled && typeof addon.cost === 'number') ? 
                                        sum + addon.cost : sum, 0) : 0
                            )) / 100
                        )}
                    /> :
                    <DeleteWorkspace onConfirm={handleDeleteWorkspaceSubscription} onCancel={() => setStates({ isOpen: false, option: 0 })} workspaceName={workspace?.workspaceName || 'workspace'} />
            }
        </Modal>
        </>
    )
}

function useWorkspaceConfigDoc(workspaceId) {
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const { getWorkspaceConfigDoc } = useDatabase();

    async function fetchConfig() {
        try {
            const docData = await getWorkspaceConfigDoc(workspaceId);
            if (docData) {
                setConfig(docData);
            } else {
                setError(true); // No config found
            }
        } catch (err) {
            setError(true);
            window.dispatchEvent(new CustomEvent('masterError', {
                detail: {
                    code: 0,
                    message: err.message,
                }
            }));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        console.log("Fetching config");
        fetchConfig();
    }, []);

    return { config, loading, fetchConfig, setConfig, error };
}

function WorkspaceNameField({ workspaceName, workspaceId }) {
    const [isEditing, setIsEditing] = useState(false);
    const [newName, setNewName] = useState(workspaceName);
    const [originalName, setOriginalName] = useState(workspaceName);
    const [isSaving, setIsSaving] = useState(false);
    const { mobile } = useOutletContext();
    const [longPressTimer, setLongPressTimer] = useState(null);
    const { url } = useAuth();
    const { post } = useAuthPost();
    
    const handleSave = async () => {
        // Don't save if the field is empty, only whitespace, or unchanged
        if (!newName.trim() || newName === originalName) {
            setNewName(originalName);
            setIsEditing(false);
            return;
        }

        setIsSaving(true);
        try {
            const [result, error, errorCode, errorMessage] = await catchErrorParse(post, `${url}/webapp/update-workspace-name`, {
                workspaceId: workspaceId,
                workspaceName: newName,
            });
            if (error) {
                window.dispatchEvent(new CustomEvent('masterError', {
                    detail: {
                        code: errorCode,
                        message: errorMessage ? errorMessage : error.message
                    }
                }));
                return;
            }
            console.log("Updated workspace name", newName);
            setOriginalName(newName);
            setIsEditing(false);
        } catch (error) {
            window.dispatchEvent(new CustomEvent('masterError', {
                detail: { message: error.message || "Failed to update workspace name" }
            }));
            setNewName(originalName);
            setIsEditing(false);
        } finally {
            setIsSaving(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !isSaving) {
            handleSave();
        } else if (e.key === 'Escape' && !isSaving) {
            setNewName(originalName);
            setIsEditing(false);
        }
    };

    const handleBlur = () => {
        if (!isSaving) {
            setNewName(originalName);
            setIsEditing(false);
        }
    };

    const startEditing = () => {
        setOriginalName(newName);
        setIsEditing(true);
    };

    const handleTouchStart = () => {
        if (mobile === false) return;
        
        const timer = setTimeout(() => {
            startEditing();
        }, 500); // 500ms for long press
        
        setLongPressTimer(timer);
    };

    const handleTouchEnd = () => {
        if (longPressTimer) {
            clearTimeout(longPressTimer);
            setLongPressTimer(null);
        }
    };

    if (isEditing) {
        return (
            <div className={styles.nameContainer}>
                <div className={`${styles.nameInnerContainer} workspace-name-container`}>
                    <div className={styles.nameField}>
                        <input
                            type="text"
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            onKeyDown={handleKeyPress}
                            onBlur={handleBlur}
                            autoFocus
                            disabled={isSaving}
                        />
                        {isSaving && (
                            <div className={styles.loadingOverlay}>
                                <ThreeDotsLoading />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.nameContainer}>
            <div className={`${styles.nameInnerContainer} workspace-name-container`}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
                onTouchCancel={handleTouchEnd}
            >
                <div className={styles.nameText}>
                    {originalName}
                </div>
                {!mobile && (
                    <FaPencil 
                        className={styles.editIcon}
                        onClick={startEditing}
                    />
                )}
            </div>
        </div>
    );
}